import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaCheckDouble } from 'react-icons/fa';
import { supabase } from '../../services/api/client';
import { fetchNotifications, markNotificationsReadByIds } from '../../services/api/realtime';
import { markNotificationRead } from '../../services/api/admin';
import { useAuth } from '../USER/Auth/AuthContext';

const NotificationBell = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!currentUser?.id) return;

    const fetchNotifs = async () => {
      const { data } = await fetchNotifications(currentUser.id);
      if (data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    };
    fetchNotifs();

    const channel = supabase
      .channel('notifications_bell')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${currentUser.id}`,
      }, payload => {
        setNotifications(prev => [payload.new, ...prev]);
        setUnreadCount(prev => prev + 1);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUser?.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
    await markNotificationRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;
    await markNotificationsReadByIds(unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setShow(!show)}
        style={{
          background: 'none',
          border: 'none',
          color: unreadCount > 0 ? 'var(--bry-accent)' : '#999',
          position: 'relative',
          cursor: 'pointer',
          padding: '4px 8px',
        }}
      >
        <FaBell size={20} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: 0,
            right: 2,
            backgroundColor: '#FF3B30',
            color: 'var(--bry-text-main)',
            fontSize: 10,
            fontWeight: 'bold',
            minWidth: 16,
            height: 16,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {show && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          width: 320,
          maxHeight: 400,
          overflowY: 'auto',
          backgroundcolor: 'var(--bry-text-main)',
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 9999,
          marginTop: 8,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f0',
          }}>
            <strong style={{ fontSize: 14 }}>Notificaciones</strong>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--bry-accent)',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                <FaCheckDouble className="me-1" />Marcar todas leídas
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#999', fontSize: 13 }}>
              No hay notificaciones
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.id}
                onClick={() => { 
                  markAsRead(n.id);
                  setShow(false);
                  if (n.link) {
                    navigate(n.link);
                  } else if (n.type === 'payout_request' || n.type === 'recharge_request') {
                    navigate('/admin/pagos');
                  } else if (n.type === 'document_uploaded') {
                    navigate('/admin/documentos');
                  } else if (n.type === 'support_ticket') {
                    navigate('/soporte');
                  } else if (n.type === 'panic_alert') {
                    navigate('/admin/spoof');
                  } else if (n.type === 'chat_message') {
                    navigate('/home');
                  }
                }}
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid #f8f8f8',
                  backgroundColor: n.read ? '#fff' : '#E6F8F9',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = n.read ? '#fff' : '#E6F8F9'}
              >
                <div style={{ fontSize: 13, fontWeight: n.read ? 'normal' : 'bold', color: '#333' }}>
                  {n.title}
                </div>
                {n.body && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                    {n.body}
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#999', marginTop: 4 }}>
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
